import { ArrowRight, CalendarRange, HardHat, Layers, PackageSearch, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, Button } from '@proerp/ui';
import { EmptyNote, PageHeader, SectionCard } from '@/components/PageLayout';
import { DemandByMonthChart } from '@/components/DemandByMonthChart';
import { StatTile } from '@/components/StatTile';
import { assemblyStatusLabel, formatAssemblyPeriod, splitAssemblyTasks } from '@/lib/formatAssembly';
import { formatExpectedDate } from '@/lib/formatForecast';
import { assemblyKpis, forecastKpis, formatQuantityByUnit } from '@/lib/portalMetrics';
import { usePortalAssembly } from '@/lib/usePortalAssembly';
import { usePortalForecast } from '@/lib/usePortalForecast';
import { usePortalUser } from '@/lib/usePortalUser';

/**
 * Übersicht des Partnerportals (ADR 0085).
 *
 * Sie beantwortet die drei Fragen, mit denen ein Partner die Seite öffnet:
 * *Was kommt als Nächstes? Wie viel ist es? Wo muss ich hin?* Kennzahlen und
 * Verlauf stehen deshalb vor den Links — eine reine Kachelwand mit „hier
 * geht's zur Bedarfsvorschau" wäre ein Inhaltsverzeichnis, keine Übersicht.
 *
 * Gefragt wird nur, was für diesen Zugang freigeschaltet ist: Eine gesperrte
 * Sicht ergäbe ein 403 und damit eine Fehlermeldung für etwas, das gar nicht
 * hätte gefragt werden dürfen.
 */
export function HomePage() {
  const user = usePortalUser();
  const canSeeForecast = user.data?.canSeeForecast ?? false;
  const canSeeAssembly = user.data?.canSeeAssembly ?? false;
  const forecast = usePortalForecast({ enabled: canSeeForecast });
  const assembly = usePortalAssembly({ enabled: canSeeAssembly });

  if (!user.data) return null;
  const hasAnyView = canSeeForecast || canSeeAssembly;

  const rows = forecast.data?.rows ?? [];
  const demand = forecastKpis(rows);
  const tasks = assembly.data?.tasks ?? [];
  const einsaetze = assemblyKpis(tasks);
  const nextTasks = splitAssemblyTasks(tasks).open.slice(0, 3);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Übersicht"
        title={`Willkommen, ${user.data.partnerName}`}
        subtitle="Hier siehst du die Daten, die PROBIG für dich freigegeben hat — als Planung, nicht als Bestellung."
      />

      {!hasAnyView && (
        <EmptyNote icon={<Layers className="size-5" aria-hidden />}>
          Für deinen Zugang ist derzeit keine Ansicht freigeschaltet. Bitte wende dich an deinen
          Ansprechpartner bei PROBIG.
        </EmptyNote>
      )}

      {hasAnyView && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {canSeeForecast && (
            <>
              <StatTile
                label="Nächster Termin"
                value={demand.nextDate ? formatExpectedDate(demand.nextDate) : '—'}
                hint={
                  demand.nextDate
                    ? formatQuantityByUnit(demand.nextByUnit)
                    : 'Noch kein Termin geplant'
                }
                icon={<CalendarRange className="size-4" aria-hidden />}
                tone="accent"
                isLoading={forecast.isPending}
              />
              <StatTile
                label="Artikel in der Vorschau"
                value={String(demand.articleCount)}
                hint={`${demand.positionCount} ${demand.positionCount === 1 ? 'Position' : 'Positionen'}`}
                icon={<PackageSearch className="size-4" aria-hidden />}
                isLoading={forecast.isPending}
              />
              <StatTile
                label="Projekte"
                value={String(demand.projectCount)}
                hint={demand.projectCount === 0 ? 'Keine Projektangabe' : 'mit Bedarf für dich'}
                icon={<Layers className="size-4" aria-hidden />}
                isLoading={forecast.isPending}
              />
            </>
          )}
          {canSeeAssembly && (
            <StatTile
              label="Offene Einsätze"
              value={String(einsaetze.openCount)}
              hint={einsaetze.undatedCount > 0 ? `${einsaetze.undatedCount} ohne Termin` : 'alle terminiert'}
              icon={<HardHat className="size-4" aria-hidden />}
              tone={einsaetze.undatedCount > 0 ? 'warning' : 'default'}
              isLoading={assembly.isPending}
            />
          )}
        </div>
      )}

      {canSeeForecast && (
        <SectionCard
          title="Bedarf je Monat"
          description="Gestapelt nach Einheit — Mengen verschiedener Einheiten werden nie zusammengezählt."
          action={
            <Button asChild variant="ghost" size="sm">
              {/* Der Linktext nennt das Ziel. „Mehr" oder „Alle" ergibt außerhalb
                  der Karte keinen Sinn — im Screenreader steht er allein. */}
              <Link to="/bedarfsvorschau">
                Zur Bedarfsvorschau
                <ArrowRight className="size-4" aria-hidden />
              </Link>
            </Button>
          }
        >
          {forecast.isPending ? (
            <div className="h-[260px] animate-pulse rounded-lg bg-muted" aria-hidden />
          ) : forecast.isError ? (
            <p className="text-sm text-muted-foreground">
              Der Verlauf ist gerade nicht abrufbar. Die Bedarfsvorschau selbst zeigt dir mehr.
            </p>
          ) : !forecast.data?.hasSupplierLink ? (
            <p className="text-sm text-muted-foreground">
              Dein Zugang ist noch keinem Lieferantenkonto zugeordnet — deshalb ist hier nichts zu
              sehen. Bitte wende dich an deinen Ansprechpartner bei PROBIG.
            </p>
          ) : (
            <DemandByMonthChart rows={rows} />
          )}
        </SectionCard>
      )}

      {canSeeAssembly && (
        <SectionCard
          title="Deine nächsten Einsätze"
          description="Die drei nächsten offenen Montagen mit Ort und Zeitraum."
          action={
            <Button asChild variant="ghost" size="sm">
              <Link to="/montagen">
                Zu den Montagen
                <ArrowRight className="size-4" aria-hidden />
              </Link>
            </Button>
          }
        >
          {assembly.isPending ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted" aria-hidden />
          ) : nextTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aktuell ist dir kein Einsatz zugeordnet. Sobald wir planen, siehst du es hier.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {nextTasks.map((task) => (
                <li
                  key={task.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border p-3"
                >
                  <Wrench className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="font-medium text-foreground">{task.title}</span>
                  <Badge tone={task.status === 'CONFIRMED' ? 'info' : 'neutral'}>
                    {assemblyStatusLabel(task.status)}
                  </Badge>
                  <span className="ml-auto text-sm tabular-nums text-muted-foreground">
                    {formatAssemblyPeriod(task.plannedStart, task.plannedEnd)}
                  </span>
                  {task.location && (
                    <span className="basis-full text-sm text-muted-foreground">{task.location}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}
    </div>
  );
}
